// Recovery from a stale bundle after a deploy.
//
// Routes are code-split (App.tsx), so a page's JavaScript is fetched at the
// moment you click it — by a file name that carries the build's content hash,
// e.g. /ops/static/assets/Contacts-Cbofel14.js. A deploy replaces every hashed
// file and the previous ones leave with the old container, so a tab that was
// open across the deploy asks for a name the server no longer has. The import
// rejects with "Failed to fetch dynamically imported module", the page never
// renders, and the error boundary takes the screen — reported 23 Sep 2026, the
// day after lazy routes landed, on an environment that deploys several times a
// day.
//
// Nothing in the tab can fix that: the file is gone. The only cure is a fresh
// index.html, which names the new chunks. Two ways there:
//   - offerNewVersion(): a page's file has already failed. The rest of the app
//     stays on screen and a toast asks the person to reload (6 Oct 2026; it
//     used to reload on its own behind a full-screen "Updating…" message, which
//     stuck when the reload landed mid-deploy and failed again).
//   - loadNewBuild(): we know a newer build is out BEFORE anything failed
//     (build-freshness.ts), and the person is navigating anyway, so the next
//     page loads the new build as an ordinary page load.
//
// The guard is a timestamp rather than a one-shot flag. A one-shot flag would
// protect the first deploy of the day and leave every later one showing the
// raw error in a long-lived tab; a short window stops a reload LOOP (which is
// what a genuinely missing chunk would otherwise cause) while still letting a
// tab recover from the next deploy an hour later.

import { notify } from "../components/hx/toast";

const KEY = "chloe-ops:chunk-reload";
const WINDOW_MS = 20_000;

/** A reload has been asked for and the document is on its way out.
 *
 * THIS IS THE PART THAT WAS MISSING. Two places catch the same failure — the
 * preload listener in main.tsx and the import's own catch in App.tsx — and a
 * reload is not instant, so the second one ran while the first was already
 * under way. It found the timestamp fresh, concluded "we just tried and it did
 * not help", and rethrew: the red error panel appeared for the second or two
 * the browser took to fetch the new document. That is what was still being
 * reported after the fix shipped (24 Sep 2026).
 *
 * The timestamp guards STARTING a reload. This guards everything after it. */
let reloading = false;

/**
 * Reload to pick up the current build, unless we just did.
 *
 * @returns true when a reload is under way — callers should render nothing
 *   and let the page go, rather than flashing an error the user cannot act
 *   on. False means we are NOT reloading and the caller must surface the
 *   error: we tried moments ago and it did not help, or sessionStorage is
 *   unavailable and the one-reload promise can't be kept.
 */
export function loadNewBuild(): boolean {
  // Already going — every later caller is told it is handled, so nobody
  // rethrows into the boundary while the document is being replaced.
  if (reloading) return true;
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < WINDOW_MS) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

let offered = false;

/** Tell the person a new version is out and let them reload when it suits
 *  them. Once per document: every later caller is a no-op. */
export function offerNewVersion(): void {
  if (offered) return;
  offered = true;
  notify({
    msg: "A new version of Helix is available. Reload to get the latest changes.",
    tone: "warn",
    sticky: true,
    action: { label: "Reload", onClick: () => window.location.reload() },
  });
}

/** True for the browser's several ways of saying "that module is not there".
 *  Chrome/Edge say "Failed to fetch dynamically imported module", Firefox
 *  "error loading dynamically imported module", Safari "Importing a module
 *  script failed". Vite's own preload helper adds "Unable to preload CSS".
 *  Matched on the message because none of them use a distinct error type. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS|error loading dynamically imported/i.test(msg);
}
